import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { Subscription, Observable } from 'rxjs';

import { restEndpoint } from 'config';
import { ApiModel, RequestOptions } from 'shared/models';
import { ImageResolution } from 'helpers';
import { DownloadCsvOption } from './download.model';

@Injectable()
export class ProductModel {
    private productsSubscription: Subscription;

    constructor(private apiModel: ApiModel) {}

    public getProducts(brandId: string, queryConditions: any): Observable<ProductSearchResult> {
        const filters = {
            identifier: _.join(queryConditions.params.identifier, '|'),
            targetGroup: _.join(_.map(queryConditions.params.targetGroup, JSON.stringify), ' | '),
            season: _.join(_.map(queryConditions.params.season, JSON.stringify), ' | ')
        };
        return this.apiModel.makeHttpObservableRequest({
            method: 'GET',
            url: `${this.apiModel.getBrandsEndpoint()}/${brandId}/products`,
            params: {
                ...queryConditions.params,
                ...filters
            }
        });
    }

    public getProductsOneRequest(brandId: string, queryConditions: any): Promise<ProductSearchResult> {
        return new Promise((resolve, reject) => {
            if (this.productsSubscription) {
                this.productsSubscription.unsubscribe();
            }

            this.productsSubscription = this.getProducts(brandId, queryConditions).subscribe(
                (response) => resolve(response),
                reject
            );
        });
    }

    public getFcProductFilters(brandId: string) {
        return this.apiModel.makeHttpRequest({
            method: 'GET',
            url: `${this.apiModel.getBrandsEndpoint()}/${brandId}/products/fcFilters`
        });
    }

    public getPvhAllowedBrands(): Observable<string[]> {
        return this.apiModel.makeHttpObservableRequest({
            method: 'GET',
            url: `${this.apiModel.getProductsEndpoint()}/pvhAllowedBrands`
        });
    }

    public getProductData(gtins: string[], language: string, imageSize: ImageResolution, initiator: string) {
        return this.getDownloadUrl({
            method: 'POST',
            url: `${this.apiModel.getProductsEndpoint()}/data/request`,
            body: { gtins, language, imageSize, initiator }
        });
    }

    public bulkGetProductData({
        gtins,
        articleNumbers,
        orderId,
        language,
        size,
        initiator,
        orderType
    }: {
        gtins?: string[];
        articleNumbers?: string[];
        orderId?: string;
        language: string;
        size: ImageResolution;
        initiator: string;
        orderType?: string;
    }): Observable<{ downloadId?: string }> {
        return this.apiModel.makeHttpObservableRequest({
            method: 'POST',
            url: `${this.apiModel.getProductsEndpoint()}/data/request/allowed`,
            body: {
                ...(gtins ? { gtins } : {}),
                ...(articleNumbers ? { articleNumbers } : {}),
                ...(orderId ? { orderId } : {}),
                ...(orderType ? { orderType } : {}),
                language,
                size,
                initiator
            }
        });
    }

    public bulkGetProductImages({
        gtins,
        articleNumbers,
        orderId,
        size,
        initiator,
        orderType,
        images,
        video,
        csv
    }: {
        gtins?: string[];
        articleNumbers?: string[];
        orderId?: string;
        size: ImageResolution;
        initiator: string;
        orderType?: string;
        images?: boolean;
        video?: boolean;
        csv?: DownloadCsvOption;
    }): Observable<{
        downloadId?: string;
        additionalData?: BulkDownloadImagesStatistics;
        downloadInformation?: BulkDownloadImagesInformation;
    }> {
        return this.apiModel.makeHttpObservableRequest({
            method: 'POST',
            url: `${this.apiModel.getDownloadsEndpoint()}/request/bulk`,
            body: {
                ...(gtins ? { gtins } : {}),
                ...(articleNumbers ? { articleNumbers } : {}),
                ...(orderId ? { orderId } : {}),
                ...(orderType ? { orderType } : {}),
                size,
                initiator,
                ...(images !== undefined ? { images } : {}),
                ...(video !== undefined ? { video } : {}),
                ...(csv ? { csv } : {})
            }
        });
    }

    private getDownloadUrl(requestDefinition: RequestOptions) {
        return this.apiModel.makeHttpRequest(requestDefinition).then((responseData) => {
            const base = `${restEndpoint}/internal`;
            return `${base}${this.apiModel.getProductsEndpoint()}/data/${responseData.downloadId}`;
        });
    }
}

export type ProductSearchResponse = ProductSearchResult | ProductSearchError;

export enum ProductSearchErrorType {
    AccessDenied = 'accessDenied',
    Unknown = 'unknown'
}

export interface BulkDownloadImagesStatistics {
    allowedCount: number;
    restrictedCount: number;
    videoCount?: number;
}

/** One omission reason with the number of GTINs it covers; shared by the video and CSV footnotes. */
export interface BulkDownloadOmissionReason {
    reason: string;
    gtinCount: number;
}

export interface BulkDownloadImagesInformation {
    searchBy: 'articleNumbers' | 'gtins';
    requested: number;
    notFound: number;
    notFoundImages: number;
    productsWithoutVideo?: number;
    videoOmitted?: boolean;
    videoOmissionReasons?: BulkDownloadOmissionReason[];
    csvOmitted?: boolean;
    csvOmissionReasons?: BulkDownloadOmissionReason[];
}

export interface BulkSearchResult {
    downloadUrl: string;
    tooManyGtins: boolean;
}

export interface BulkSearchResultWithInformation extends BulkSearchResult {
    additionalData?: BulkDownloadImagesStatistics;
    downloadInformation?: BulkDownloadImagesInformation;
}

export interface ProductSearchError {
    error: ProductSearchErrorType;
}

export interface ProductSearchResult {
    products: {
        colorGroupId: string;
        styleGroupId: string;
        variants: Product[];
    }[];
    count: number;
    facets?: {
        seasons: Facet[];
        targetGroups: Facet[];
        categories: Facet[];
        ageGroups: Facet[];
        colors: Facet[];
    };
}

interface Facet {
    key: string;
    count: number;
}

interface Product {
    _id: string;
    articleNumber: string;
    articleNumberSimplified: string;
    brand: string;
    colorGroupId: string;
    gtin: string;
    created: Date;
    fcAttributes: any;
    fcKeys: any;
    hasFrontImage: boolean;
    hasImages: boolean;
    hash: string;
    manufacturerAttributes: any;
    media: any;
    organization: string;
    state: string;
    styleGroupId: string;
    updated: Date;
    visibleFromDate: Date;
}
